import React from "react";

import {
  Icon,
  LaptopIcon,
  PackageIcon,
  WatchIcon,
  GemIcon,
  ShirtIcon,
  AmphoraIcon,
  SprayCanIcon,
  Disc3Icon,
  WineIcon,
  IdCardIcon,
  BookCopy,
  FishSymbolIcon,
  UsersRoundIcon,
  BoxIcon,
  CarIcon,
  TicketIcon,
  HeartHandshakeIcon,
  // VRG-610 categories. All four verified present in the pinned lucide-react@0.468.0
  // (node_modules/lucide-react/dist/esm/icons/{cannabis,pill-bottle,tablets,scale}.js;
  // `Scale` is exported under both names, `Scale` and `Scale as ScaleIcon`); no version
  // bump was needed. Same glyphs `Cells.tsx` renders, so the two icon maps agree rather
  // than each picking their own.
  CannabisIcon,
  PillBottleIcon,
  TabletsIcon,
  ScaleIcon,
} from "lucide-react";
import {
  gemRing,
  bagHand,
  highHeel,
  dress,
  wineGlassBottle,
  glassesSun,
} from "@lucide/lab";

/**
 * Canonical product-CATEGORY → icon mapping. Single source for the icons shown
 * on the Products table (CategoryCell) and the Type selector on the Edit form,
 * so they stay identical. `category` is normalized (lowercased, spaces/`&` → `_`).
 * Unknown values fall back to a generic box icon.
 *
 * ⚠️ CATEGORIES ONLY — 23 of them. A product TYPE (68, the tag shop's
 * `best_for_product_types`) belongs in the SIBLING map, `getProductTypeIcon` in
 * `productTypeIcon.tsx`, and is held to total coverage by
 * `product-type-icons.guard.spec.ts`. Adding a type here instead looks like it
 * works — 14 type names collide with a category slug, so some of them resolve by
 * coincidence — which is precisely the coincidence VRG-628 separated the two maps
 * to remove. Two taxonomies, two accessors; pick by which list the string is from.
 */
export const getCategoryIcon = (category: string, size = 20): JSX.Element => {
  const p = { strokeWidth: 1 as const, size };
  const icons: Record<string, JSX.Element> = {
    subscription: <LaptopIcon {...p} />,
    order: <PackageIcon {...p} />,
    watches: <WatchIcon {...p} />,
    gemstones: <GemIcon {...p} />,
    jewelry: <Icon iconNode={gemRing} {...p} />,
    apparel: <ShirtIcon {...p} />,
    art: <AmphoraIcon {...p} />,
    perfume: <SprayCanIcon {...p} />,
    music: <Disc3Icon {...p} />,
    wine: <WineIcon {...p} />,
    wine_spirits: <Icon iconNode={wineGlassBottle} {...p} />,
    handbags: <Icon iconNode={bagHand} {...p} />,
    trading_cards: <IdCardIcon {...p} />,
    footwear: <Icon iconNode={highHeel} {...p} />,
    eyewear: <Icon iconNode={glassesSun} {...p} />,
    books: <BookCopy {...p} />,
    dresses: <Icon iconNode={dress} {...p} />,
    caviar: <FishSymbolIcon {...p} />,
    memberships: <UsersRoundIcon {...p} />,
    membership_cards: <UsersRoundIcon {...p} />,
    clothes: <Icon iconNode={dress} {...p} />,
    glasses: <Icon iconNode={glassesSun} {...p} />,
    bags: <Icon iconNode={bagHand} {...p} />,
    collectibles: <HeartHandshakeIcon {...p} />,
    automotive: <CarIcon {...p} />,
    event_tickets: <TicketIcon {...p} />,
    // VRG-610 added these four to PRODUCT_CATEGORIES with no row here, so all four
    // fell through to the `?? <BoxIcon>` below and drew a generic box.
    cannabis: <CannabisIcon {...p} />,
    pharmaceuticals: <PillBottleIcon {...p} />,
    // Tablets (two pills), not Pill (one capsule) — "pills", plural, and it is what
    // Cells.tsx already uses for this slug.
    supplements: <TabletsIcon {...p} />,
    // A balance scale, not a document and not a stamp. FileText was the first pass's
    // answer because Cells.tsx already drew `documents:` that way, but it describes the
    // MEDIUM (a page) rather than the category (legal instruments) — and every other row
    // here names its subject. Scale names the subject and is not taken: no glyph in this
    // map or in Cells.tsx uses it, and `stamps` keeps StampIcon, so nothing collides.
    legal_documents: <ScaleIcon {...p} />,
    other: <BoxIcon {...p} />,
  };
  const key = (category || "").trim().toLowerCase().replace(/[\s&]+/g, "_");
  return icons[key] ?? <BoxIcon {...p} />;
};
